import { type ReactNode } from "react";
import { InputAdornment } from "@mui/material";

import { Box } from "ui/box/box";
import { VALUE_ALERT_ICON_SX } from "ui/select/select.styles";

import { INPUT_ICON_CLASS } from "theme/components/field-geometry";

import Icons from "assets";

import type { OptionType } from "ui/select/select.types";

/**
 * The start slot holds `startIcon`, then the selected option's own `icon`; the end slot the option's
 * alert glyph, then the select's `endIcon`, which shows with or without a value — Figma draws it on an
 * empty select too. Each is an `InputAdornment` round `.ApolloInput-icon` wrappers, so the
 * `MuiOutlinedInput` geometry sizes, colours and spaces them exactly as on every other field.
 */
export const SelectValueAdornment = ({
  option,
  slot,
  startIcon,
  endIcon,
}: {
  option: OptionType | null;
  slot: "start" | "end";
  startIcon?: ReactNode;
  endIcon?: ReactNode;
}) => {
  if (slot === "start") {
    if (!startIcon && !option?.icon) {
      return null;
    }

    return (
      <InputAdornment position="start">
        {startIcon && (
          <Box component="span" className={INPUT_ICON_CLASS}>
            {startIcon}
          </Box>
        )}
        {option?.icon && (
          <Box component="span" className={INPUT_ICON_CLASS}>
            {option.icon}
          </Box>
        )}
      </InputAdornment>
    );
  }

  const showAlert = !!option?.isAlertShown;

  if (!endIcon && !showAlert) {
    return null;
  }

  return (
    <InputAdornment position="end">
      {showAlert && (
        <Box component="span" className={INPUT_ICON_CLASS}>
          <Box component={Icons.ExclamationCircle} sx={VALUE_ALERT_ICON_SX} />
        </Box>
      )}
      {endIcon && (
        <Box component="span" className={INPUT_ICON_CLASS}>
          {endIcon}
        </Box>
      )}
    </InputAdornment>
  );
};
